import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseCellRendererComponent } from '@examdojo/ag-grid';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { PayoutRequestUIModel } from '@examdojo/payments';
import { PayoutRequestsComponent } from './payout-requests.component';

@Component({
  selector: 'dojo-payout-request-actions-cell-renderer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex h-full items-center justify-end gap-2">
      @if (hasReceipt) {
        <dojo-button [fill]="'solid'" [size]="'xs'" [customColor]="'orange'" (click)="onViewReceipt($event)">
          <dojo-icon [icon]="'file-pdf'" class="mr-1" [size]="14" />
          View Invoice
        </dojo-button>
      }

      @if (request?.isEditable) {
        <dojo-button [fill]="'outline'" [size]="'xs'" (click)="onEdit($event)">
          <dojo-icon [icon]="'pencil'" class="mr-1" [size]="14" />
          Edit
        </dojo-button>
      }

      @if (request?.isEditable && (request?.totalHours ?? 0) > 0) {
        <div [matTooltip]="submitTooltip" [matTooltipDisabled]="canSubmit">
          <dojo-button [fill]="'solid'" [size]="'xs'" [disabled]="!canSubmit" (click)="onSubmit($event)">
            <dojo-icon [icon]="'paper-plane'" class="mr-1" [size]="14" />
            Submit
          </dojo-button>
        </div>
      }

      @if (request?.status === 'WAITING_FOR_APPROVAL' || request?.status === 'REJECTED') {
        <dojo-button [fill]="'outline'" [size]="'xs'" (click)="onWithdraw($event)">
          <dojo-icon [icon]="'arrow-rotate-left'" class="mr-1" [size]="14" />
          Revert to Draft to edit
        </dojo-button>
      }
    </div>
  `,
  imports: [ButtonComponent, IconComponent, MatTooltipModule],
})
export class PayoutRequestActionsCellRendererComponent extends BaseCellRendererComponent<
  unknown,
  PayoutRequestUIModel
> {
  constructor(private readonly cdr: ChangeDetectorRef) {
    super();
  }

  override refresh(params: Parameters<typeof this.agInit>[0]): boolean {
    super.refresh(params);
    console.log('PayoutRequestActionsCellRenderer - refresh:', {
      requestId: params.data?.id,
      status: params.data?.status,
      isEditable: params.data?.isEditable,
    });
    this.cdr.markForCheck();
    return true;
  }

  get request(): PayoutRequestUIModel | undefined {
    return this.params?.data;
  }

  get parentComponent(): PayoutRequestsComponent | undefined {
    return this.params?.context?.componentParent as PayoutRequestsComponent | undefined;
  }

  get canSubmit(): boolean {
    if (!this.request?.id || !this.parentComponent) {
      return false;
    }
    return this.parentComponent.canSubmitRequest(this.request.id);
  }

  get submitTooltip(): string {
    if (!this.request?.id || !this.parentComponent) {
      return '';
    }
    return this.parentComponent.getSubmitDisabledTooltip(this.request.id);
  }

  get hasReceipt(): boolean {
    const status = this.request?.status;

    if (status !== 'PAYMENT_SUCCEEDED') {
      return false;
    }

    const attachments = this.request?.attachments as Array<{ url: string; type: string }> | null | undefined;
    if (!Array.isArray(attachments)) {
      return false;
    }

    const invoice = attachments.find((att) => att.type === 'invoice');
    return !!invoice?.url;
  }

  onViewReceipt(event: Event): void {
    event.stopPropagation();
    if (this.request) {
      this.parentComponent?.openReceiptViewerDialog(this.request);
    }
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    if (this.request) {
      this.parentComponent?.openWorkingHoursDialog(this.request);
    }
  }

  onSubmit(event: Event): void {
    event.stopPropagation();
    if (this.request?.id && this.canSubmit) {
      this.parentComponent?.submitPayoutRequest(this.request.id);
    }
  }

  onWithdraw(event: Event): void {
    event.stopPropagation();
    if (this.request?.id) {
      this.parentComponent?.withdrawPayoutRequest(this.request.id);
    }
  }
}
